/**
 * /finance/pay-runs — Pay Run Cockpit (PR3)
 * ──────────────────────────────────────────
 * Read-and-release surface for every completed job's contractor / labor
 * settlement. Sits next to /accounting; built on canonical resolvers.
 *
 * Architecture:
 *   • READS  — Job, Contractor, InstallerWrapRecord, ContractorPayProfile,
 *              Callback, HoldbackState, DisputeState, PayRunRecord  (all AppDataContext)
 *   • RESOLVE — getJobPayRun() per (job, primary party) pair
 *   • WRITE   — recordPayRun() — only fires from the "Release" button on Ready rows
 *
 * SubPivots:
 *   • Ready — releasable=true, not yet recorded
 *   • Held  — releasable=false, with structured hold reason chips
 *   • Paid  — append-only PayRunRecord history
 *
 * Role gate: super_admin | admin | accounting | ops_manager
 *   (Field/installer/sub roles never see this surface — they have no
 *    pricing/cost visibility per project policy.)
 *
 * Non-goals here:
 *   • Internal-crew labor planning UI (PR4)
 *   • Margin reconciliation (PR5)
 *   • Edge-function forwarding (later wave)
 */
import { useMemo, useState } from "react";
import { VarianceDetailDrawer, type VarianceDetailContext } from "@/components/finance/VarianceDetailDrawer";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { SubPivot } from "@/components/cockpit/SubPivot";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { CheckCircle2, AlertTriangle, History, TrendingUp, Download } from "lucide-react";
import { exportCsv, stampFilename } from "@/lib/csvExport";

import { type PayRunHoldReason, type PayRunView } from "@/data/finance/payRun/getJobPayRun";
import { HOLD_REASON_LABEL, HOLD_REASON_OWNER } from "@/data/finance/payRun/holdReasonCopy";
import { formatHeldAge, getHeldAgeTone } from "@/data/finance/payRun/getPayRunHoldAge";

import { recordPayRun } from "@/data/finance/payRun/recordPayRun";
import { postPayRunToLedger, summarizeLedgerPost } from "@/data/finance/payRun/postPayRunToLedger";
import { useExpenseLedgerContext } from "@/data/jobCost/ExpenseLedgerContext";
import CrewLeadCloseoutDialog from "@/components/finance/CrewLeadCloseoutDialog";
import SubmitFieldAddDialog from "@/components/finance/SubmitFieldAddDialog";
import {
  getEffectiveFieldAdds, getPendingFieldAdds, decideFieldAddSubmission,
  type FieldAddSubmission,
} from "@/data/finance/payRun/fieldAdds";
import { getPayProfile } from "@/data/contractorPayProfile";
import { deriveAllHoldbackStates } from "@/data/contractor/networkExecution/holdback/deriveState";
import { deriveAllDisputes } from "@/data/contractor/networkExecution/dispute/dispute";
import { usePayRunViews, type PayRunRow as Row } from "@/data/finance/payRun/usePayRunViews";
import { useSeedPayRunActivity } from "@/data/finance/payRun/useSeedPayRunActivity";
import type { PayRunRecord } from "@/data/finance/payRun/recordPayRun";
import type { Job } from "@/data/types";
import {
  DRIFT_KIND_LABEL,
  type LaborVarianceSignal, type PayRunReconciliation,
  type VarianceSeverity, type VarianceRollup,
} from "@/data/finance/payRun/varianceAnalysis";
import { ClipboardEdit, Plus, ThumbsUp, ThumbsDown } from "lucide-react";

const ALLOWED_ROLES = new Set([
  "super_admin", "owner", "admin", "ops_manager", "cfo", "finance_manager", "bookkeeper",
]);

type Pivot = "ready" | "held" | "approvals" | "variance" | "paid";

const APPROVAL_ROLES = new Set(["super_admin", "owner", "admin", "cfo", "finance_manager", "bookkeeper"]);

// HOLD_REASON_LABEL / HOLD_REASON_OWNER live in `holdReasonCopy.ts` so the
// `/accounting` surface can reuse the exact same copy. Imported above.


const PARTY_LABEL = {
  internal_crew: "Internal crew",
  external_contractor: "Contractor",
  external_subcontractor: "Subcontractor",
} as const;

// `Row` is re-exported from the canonical hook as `PayRunRow`.
// Local alias kept above via `import { ... as Row }`.


export default function PayRunsCockpitPage() {
  useSeedPayRunActivity();
  const { role } = useRole();
  const {
    jobs, contractors, payProfiles, installerWrapRecords,
    callbacks, holdbackLedger, disputeEvents,
  } = useAppData();
  const _appData = useAppData() as any;
  const payRunRecords: any[] = _appData.payRunRecords ?? [];
  const setPayRunRecords = _appData.setPayRunRecords ?? (() => {});
  const laborActualRecords: any[] = _appData.laborActualRecords ?? [];
  const fieldAddSubmissions: any[] = _appData.fieldAddSubmissions ?? [];
  const setFieldAddSubmissions = _appData.setFieldAddSubmissions ?? (() => {});
  const { appendEntries } = useExpenseLedgerContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPivot = ((): Pivot => {
    const p = searchParams.get("pivot");
    return (p === "ready" || p === "held" || p === "approvals" || p === "variance" || p === "paid") ? p : "ready";
  })();
  const [pivot, setPivotState] = useState<Pivot>(initialPivot);
  const setPivot = (next: Pivot) => {
    setPivotState(next);
    const sp = new URLSearchParams(searchParams);
    if (next === "ready") sp.delete("pivot"); else sp.set("pivot", next);
    setSearchParams(sp, { replace: true });
  };
  const [closeoutFor, setCloseoutFor] = useState<Row | null>(null);
  const [submitFieldAddFor, setSubmitFieldAddFor] = useState<Row | null>(null);
  const [paidDetail, setPaidDetail] = useState<VarianceDetailContext | null>(null);

  const contractorById = useMemo(() => {
    const m = new Map(contractors.map((c) => [c.id, c]));
    return m;
  }, [contractors]);

  const callbacksByJob = useMemo(() => {
    const m = new Map<string, typeof callbacks>();
    for (const c of callbacks) {
      const arr = m.get(c.jobId) ?? [];
      arr.push(c);
      m.set(c.jobId, arr);
    }
    return m;
  }, [callbacks]);

  const holdbackStates = useMemo(() => deriveAllHoldbackStates(holdbackLedger), [holdbackLedger]);
  const disputeStates = useMemo(() => deriveAllDisputes(disputeEvents), [disputeEvents]);


  // PR8.5 — canonical hook resolves rows, ready/held partitioning,
  // variance signals, reconciliations, and rollup in one place. Other
  // surfaces (Accounting, JobCostBreakdownPanel) will consume this same
  // hook, so the join logic is no longer duplicated here.
  const {
    rows, rowByJob, readyRows, heldRows,
    laborSignals, reconciliations, varianceRollup,
  } = usePayRunViews();

  const varianceBadgeCount =
    varianceRollup.laborCritical + varianceRollup.laborWarn + varianceRollup.reconciliationDrift;

  const counts = { ready: readyRows.length, held: heldRows.length, paid: payRunRecords.length };

  // Keep rowByJob in scope for any downstream consumers (no-op marker).
  void rowByJob;



  const handleRelease = async (row: Row) => {
    const job = jobs.find((j) => j.id === row.job.id);
    if (!job) return;
    const contractor = row.contractorId ? contractorById.get(row.contractorId) : undefined;
    const wrap = installerWrapRecords.find((w) => w.jobId === row.job.id);
    const fieldAdds = getEffectiveFieldAdds(job.id, row.contractorId ?? null, fieldAddSubmissions);
    const result = recordPayRun({
      job,
      contractor,
      payProfile: contractor ? getPayProfile(payProfiles, contractor.id) : undefined,
      wrapRecord: wrap,
      callbacks: callbacksByJob.get(job.id) ?? [],
      holdbacks: holdbackStates.filter((h) => h.jobId === job.id),
      disputes: disputeStates.filter((d) => d.jobId === job.id),
      releasedBy: role,
      existing: payRunRecords,
      fieldAdds,
    });
    if (result.ok !== true) {
      const reason = (result as { reason?: PayRunHoldReason }).reason as PayRunHoldReason | undefined;
      toast({
        title: "Can't release pay run",
        description: (reason && HOLD_REASON_LABEL[reason]) ?? "Not releasable.",
        variant: "destructive",
      });
      return;
    }
    if (result.idempotent) {
      toast({ title: "Already released", description: `Pay run ${result.record.id}` });
      return;
    }
    setPayRunRecords((prev) => [...prev, result.record]);
    toast({
      title: "Pay run released",
      description: `${row.job.customerName} · $${result.record.netReleased.toLocaleString()}`,
    });

    // PR6 — bridge to canonical expense ledger. Remote write (Edge Function)
    // when Cloud is configured + organizationId resolves; local mirror always.
    try {
      const orgId = (job as { organizationId?: string }).organizationId;
      const post = await postPayRunToLedger(result.record, { organizationId: orgId });
      appendEntries(post.mirrored);
      const summary = summarizeLedgerPost(post);
      if (post.hadErrors) {
        toast({
          title: "Ledger post had errors",
          description: summary,
          variant: "destructive",
        });
      } else {
        toast({ title: "Posted to ledger", description: summary });
      }
    } catch (e) {
      toast({
        title: "Ledger post failed",
        description: e instanceof Error ? e.message : "Pay run released; retry from history.",
        variant: "destructive",
      });
    }
  };

  if (!ALLOWED_ROLES.has(role)) {
    return (
      <CockpitPage title="Pay Runs" maxWidth="7xl">
        <Card className="shadow-none border-border/60 bg-transparent p-8 text-center text-sm text-muted-foreground">
          You don't have access to pay runs.
        </Card>
      </CockpitPage>
    );
  }

  const pendingApprovals = useMemo(() => getPendingFieldAdds(fieldAddSubmissions), [fieldAddSubmissions]);
  const canApprove = APPROVAL_ROLES.has(role);

  const decide = (sub: FieldAddSubmission, decision: "approved" | "rejected") => {
    try {
      const next = decideFieldAddSubmission({ submission: sub, decision, decidedBy: role });
      setFieldAddSubmissions((prev) => [...prev, next]);
      toast({
        title: decision === "approved" ? "Field-add approved" : "Field-add rejected",
        description: `$${sub.amount.toLocaleString()} · job ${sub.jobId}`,
      });
    } catch (e) {
      toast({
        title: "Decision failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  // Rollup strip — dollarized totals so a CFO can triage between pivots
  // without clicking each one. Pure derivation from already-resolved data.
  const readyDollars = readyRows.reduce((s, r) => s + r.view.netReleasable, 0);
  const heldDollars = heldRows.reduce(
    (s, r) => s + r.view.baseAmount + r.view.fieldAddPendingTotal, 0,
  );
  const pendingApprovalDollars = pendingApprovals.reduce((s, p) => s + p.amount, 0);
  const paidDollars = payRunRecords.reduce((s, r) => s + r.netReleased, 0);
  const varianceDriftDollars = Math.abs(varianceRollup.totalNetDelta);

  return (
    <CockpitPage title="Pay Runs" maxWidth="7xl">
      <div data-tour-id="pay-runs-header" />
      <BooksNavRail />
      <Card className="shadow-none border-border/60 bg-transparent p-3 mb-3 grid grid-cols-2 md:grid-cols-5 gap-3">
        <RollupCell label="Ready" value={`$${readyDollars.toLocaleString()}`} sub={`${counts.ready} run${counts.ready === 1 ? "" : "s"}`} tone="foreground" onClick={() => setPivot("ready")} />
        <RollupCell label="Held" value={`$${heldDollars.toLocaleString()}`} sub={`${counts.held} row${counts.held === 1 ? "" : "s"}`} tone={heldDollars > 0 ? "warn" : "muted"} onClick={() => setPivot("held")} />
        <RollupCell label="Approvals" value={`$${pendingApprovalDollars.toLocaleString()}`} sub={`${pendingApprovals.length} pending`} tone={pendingApprovals.length > 0 ? "warn" : "muted"} onClick={() => setPivot("approvals")} />
        <RollupCell label="Drift" value={`$${varianceDriftDollars.toLocaleString()}`} sub={`${varianceBadgeCount} signal${varianceBadgeCount === 1 ? "" : "s"}`} tone={varianceRollup.laborCritical > 0 ? "critical" : varianceBadgeCount > 0 ? "warn" : "muted"} onClick={() => setPivot("variance")} />
        <RollupCell label="Paid" value={`$${paidDollars.toLocaleString()}`} sub={`${counts.paid} to date`} tone="muted" onClick={() => setPivot("paid")} />
      </Card>
      <SubPivot<Pivot>
        value={pivot}
        onChange={setPivot}
        ariaLabel="Pay run state"
        options={[
          { id: "ready",     label: `Ready · ${counts.ready}`,            icon: <CheckCircle2 className="h-3.5 w-3.5" /> },
          { id: "held",      label: `Held · ${counts.held}`,              icon: <AlertTriangle className="h-3.5 w-3.5" /> },
          { id: "approvals", label: `Approvals · ${pendingApprovals.length}`, icon: <ThumbsUp className="h-3.5 w-3.5" /> },
          { id: "variance",  label: `Variance · ${varianceBadgeCount}`,   icon: <TrendingUp className="h-3.5 w-3.5" /> },
          { id: "paid",      label: `Paid · ${counts.paid}`,              icon: <History className="h-3.5 w-3.5" /> },
        ]}
      />

      {pivot === "ready" && (
        <ReadyTable rows={readyRows} onRelease={handleRelease} onLogLabor={setCloseoutFor} onSubmitFieldAdd={setSubmitFieldAddFor} />
      )}
      {pivot === "held" && <HeldTable rows={heldRows} onLogLabor={setCloseoutFor} onSubmitFieldAdd={setSubmitFieldAddFor} />}
      {pivot === "approvals" && (
        <ApprovalsTable
          pending={pendingApprovals}
          jobs={jobs}
          canApprove={canApprove}
          onDecide={decide}
        />
      )}
      {pivot === "variance" && (
        <VariancePanel
          rollup={varianceRollup}
          laborSignals={laborSignals}
          reconciliations={reconciliations}
          payRunRecords={payRunRecords}
          jobs={jobs}
        />
      )}
      {pivot === "paid" && (
        <PaidTable
          onSelect={(record, customerName) =>
            setPaidDetail({ kind: "paid", record, customerName })
          }
        />
      )}

      <VarianceDetailDrawer
        open={paidDetail != null}
        onOpenChange={(o) => { if (!o) setPaidDetail(null); }}
        context={paidDetail}
      />


      {closeoutFor && (
        <CrewLeadCloseoutDialog
          open={!!closeoutFor}
          onOpenChange={(o) => !o && setCloseoutFor(null)}
          jobId={closeoutFor.job.id}
          jobLabel={closeoutFor.job.customerName}
          estimatedDollars={closeoutFor.estimatedLabor ?? undefined}
        />
      )}

      {submitFieldAddFor && (
        <SubmitFieldAddDialog
          open={!!submitFieldAddFor}
          onOpenChange={(o) => !o && setSubmitFieldAddFor(null)}
          jobId={submitFieldAddFor.job.id}
          jobLabel={submitFieldAddFor.job.customerName}
          partyId={submitFieldAddFor.contractorId ?? null}
        />
      )}
    </CockpitPage>
  );
}

// ── Subviews ─────────────────────────────────────────────────────────────

function ReadyTable({
  rows, onRelease, onLogLabor, onSubmitFieldAdd,
}: { rows: Row[]; onRelease: (r: Row) => void; onLogLabor: (r: Row) => void; onSubmitFieldAdd: (r: Row) => void }) {
  if (rows.length === 0) {
    return <Card className="shadow-none border-border/60 bg-transparent p-8 text-center text-sm text-muted-foreground">Nothing ready to release.</Card>;
  }
  // Sort: largest net-releasable first. CFO reviews the biggest releases first.
  const sorted = [...rows].sort((a, b) => b.view.netReleasable - a.view.netReleasable);
  const tBase = sorted.reduce((s, r) => s + r.view.baseAmount, 0);
  const tAdds = sorted.reduce((s, r) => s + r.view.fieldAddApprovedTotal, 0);
  const tHold = sorted.reduce((s, r) => s + r.view.holdbackHeld, 0);
  const tNet = sorted.reduce((s, r) => s + r.view.netReleasable, 0);
  const handleExport = () => {
    exportCsv(
      stampFilename("payruns_ready"),
      ["Job ID", "Customer", "Party kind", "Party", "Base", "Field adds", "Holdback", "Net releasable"],
      sorted.map((r) => [
        r.job.id, r.job.customerName, r.view.partyKind, r.view.partyName ?? "",
        r.view.baseAmount, r.view.fieldAddApprovedTotal, r.view.holdbackHeld, r.view.netReleasable,
      ]),
    );
  };
  return (
    <Card className="shadow-none border-border/60 bg-transparent p-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <div className="text-[11px] text-muted-foreground">{sorted.length} run{sorted.length === 1 ? "" : "s"} ready to release</div>
        <Button size="sm" variant="ghost" onClick={handleExport} title="Export rows as CSV">
          <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Job</TableHead>
            <TableHead>Party</TableHead>
            <TableHead className="text-right">Base</TableHead>
            <TableHead className="text-right">+ Field adds</TableHead>
            <TableHead className="text-right">− Holdback</TableHead>
            <TableHead className="text-right">Net releasable</TableHead>
            <TableHead className="text-right"> </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((r) => (
            <TableRow key={`${r.job.id}-${r.view.partyId ?? "x"}`}>
              <TableCell>
                <div className="font-medium text-[12px]">{r.job.customerName}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{r.job.id}</div>
              </TableCell>
              <TableCell className="text-[11px]">
                <div>{r.view.partyName ?? "—"}</div>
                <div className="text-muted-foreground text-[10px]">{PARTY_LABEL[r.view.partyKind]}</div>
              </TableCell>
              <TableCell className="text-right font-mono text-[11px]">${r.view.baseAmount.toLocaleString()}</TableCell>
              <TableCell className="text-right font-mono text-[11px]">
                ${r.view.fieldAddApprovedTotal.toLocaleString()}
              </TableCell>
              <TableCell className="text-right font-mono text-[11px]">${r.view.holdbackHeld.toLocaleString()}</TableCell>
              <TableCell className="text-right font-mono text-[12px] font-semibold">
                ${r.view.netReleasable.toLocaleString()}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  {r.view.partyKind === "internal_crew" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onLogLabor(r)}
                      title="Log Crew Lead actuals"
                    >
                      <ClipboardEdit className="h-3.5 w-3.5 mr-1" /> Labor
                    </Button>
                  )}
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => onSubmitFieldAdd(r)}
                    title="Submit field-add"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Field-add
                  </Button>
                  <Button size="sm" onClick={() => onRelease(r)} data-testid={`payrun-release-${r.job.id}`}>
                    Release
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <tfoot className="border-t border-border/60 bg-muted/30">
          <tr className="text-[11px]">
            <td className="py-2.5 px-4 font-medium text-muted-foreground" colSpan={2}>
              About to release · {sorted.length} run{sorted.length === 1 ? "" : "s"}
            </td>
            <td className="py-2.5 px-4 text-right font-mono tabular-nums text-muted-foreground">${tBase.toLocaleString()}</td>
            <td className="py-2.5 px-4 text-right font-mono tabular-nums text-muted-foreground">${tAdds.toLocaleString()}</td>
            <td className="py-2.5 px-4 text-right font-mono tabular-nums text-muted-foreground">${tHold.toLocaleString()}</td>
            <td className="py-2.5 px-4 text-right font-mono tabular-nums font-semibold text-foreground">${tNet.toLocaleString()}</td>
            <td />
          </tr>
        </tfoot>
      </Table>
    </Card>
  );
}


function HeldTable({
  rows, onLogLabor, onSubmitFieldAdd,
}: { rows: Row[]; onLogLabor: (r: Row) => void; onSubmitFieldAdd: (r: Row) => void }) {
  if (rows.length === 0) {
    return <Card className="shadow-none border-border/60 bg-transparent p-8 text-center text-sm text-muted-foreground">No pay runs on hold.</Card>;
  }

  // Sort: rows with known age first (oldest → newest), unknown-age last.
  // Aging is the CFO's primary triage signal; show the worst at the top.
  const sorted = [...rows].sort((a, b) => {
    const aMs = a.heldAge?.ageMs ?? -1;
    const bMs = b.heldAge?.ageMs ?? -1;
    if (aMs === bMs) return 0;
    if (aMs < 0) return 1;
    if (bMs < 0) return -1;
    return bMs - aMs;
  });

  const totalHeld = sorted.reduce(
    (sum, r) => sum + r.view.baseAmount + r.view.fieldAddPendingTotal,
    0,
  );
  const oldestAgeMs = sorted.reduce(
    (m, r) => (r.heldAge && r.heldAge.ageMs > m ? r.heldAge.ageMs : m),
    0,
  );
  const anyKnownAge = sorted.some((r) => r.heldAge != null);

  const toneClass = (tone: "fresh" | "warn" | "critical") =>
    tone === "critical"
      ? "text-destructive font-semibold"
      : tone === "warn"
      ? "text-warning"
      : "text-foreground";

  const handleExport = () => {
    exportCsv(
      stampFilename("payruns_held"),
      ["Job ID", "Customer", "Party", "Hold reasons", "Owners", "Held age", "Base", "Pending adds"],
      sorted.map((r) => [
        r.job.id, r.job.customerName, r.view.partyName ?? PARTY_LABEL[r.view.partyKind],
        r.view.holdReasons.map((k) => HOLD_REASON_LABEL[k]).join(" · "),
        r.view.holdReasons.map((k) => HOLD_REASON_OWNER[k]).join(" · "),
        r.heldAge ? formatHeldAge(r.heldAge.ageMs) : "",
        r.view.baseAmount, r.view.fieldAddPendingTotal,
      ]),
    );
  };
  return (
    <Card className="shadow-none border-border/60 bg-transparent p-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <div className="text-[11px] text-muted-foreground">{sorted.length} row{sorted.length === 1 ? "" : "s"} on hold</div>
        <Button size="sm" variant="ghost" onClick={handleExport} title="Export rows as CSV">
          <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Job</TableHead>
            <TableHead>Party</TableHead>
            <TableHead>Why held</TableHead>
            <TableHead title="Time since the oldest hold reason was triggered. Shows ‘—’ only if a contributing reason has no timestamp in the data model.">
              Held
            </TableHead>
            <TableHead className="text-right">Base</TableHead>
            <TableHead className="text-right">Pending adds</TableHead>
            <TableHead className="text-right"> </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((r) => {
            const age = r.heldAge;
            return (
              <TableRow key={`${r.job.id}-${r.view.partyId ?? "x"}`}>
                <TableCell>
                  <div className="font-medium text-[12px]">{r.job.customerName}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{r.job.id}</div>
                </TableCell>
                <TableCell className="text-[11px]">{r.view.partyName ?? PARTY_LABEL[r.view.partyKind]}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {r.view.holdReasons.map((k) => (
                      <div key={k} className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[10px] border-warning text-warning">
                          {HOLD_REASON_LABEL[k]}
                        </Badge>
                        <span
                          className="text-[10px] text-muted-foreground tabular-nums"
                          title={`Owner: ${HOLD_REASON_OWNER[k]}`}
                        >
                          · {HOLD_REASON_OWNER[k]}
                        </span>
                      </div>
                    ))}
                  </div>
                </TableCell>

                <TableCell className={`text-[11px] tabular-nums ${age ? toneClass(getHeldAgeTone(age.ageMs)) : "text-muted-foreground"}`}>
                  {age ? (
                    <span title={`Held since ${format(new Date(age.heldSince), "MMM d, h:mm a")} · oldest reason: ${HOLD_REASON_LABEL[age.contributingReason]}`}>
                      {formatHeldAge(age.ageMs)}
                    </span>
                  ) : (
                    <span title="Age unavailable — a contributing hold reason has no timestamp source in the current data model.">
                      —
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-[11px]">${r.view.baseAmount.toLocaleString()}</TableCell>
                <TableCell className="text-right font-mono text-[11px]">
                  ${r.view.fieldAddPendingTotal.toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {r.view.partyKind === "internal_crew" && (
                      <Button size="sm" variant="ghost" onClick={() => onLogLabor(r)}>
                        <ClipboardEdit className="h-3.5 w-3.5 mr-1" /> Labor
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => onSubmitFieldAdd(r)}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Field-add
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <tfoot className="border-t border-border/60 bg-muted/30">
          <tr className="text-[11px]">
            <td className="py-2.5 px-4 font-medium text-muted-foreground" colSpan={3}>
              Total held · {sorted.length} row{sorted.length === 1 ? "" : "s"}
            </td>
            <td className={`py-2.5 px-4 tabular-nums ${anyKnownAge ? toneClass(getHeldAgeTone(oldestAgeMs)) : "text-muted-foreground"}`}>
              {anyKnownAge ? `oldest ${formatHeldAge(oldestAgeMs)}` : "—"}
            </td>
            <td className="py-2.5 px-4 text-right font-mono font-semibold text-warning" colSpan={2}>
              ${totalHeld.toLocaleString()}
            </td>
            <td />
          </tr>
        </tfoot>
      </Table>
    </Card>
  );
}



function PaidTable({ onSelect }: { onSelect: (record: PayRunRecord, customerName: string) => void }) {
  const _appData2 = useAppData() as any;
  const payRunRecords: any[] = _appData2.payRunRecords ?? [];
  const { jobs } = _appData2;
  const customerByJob = new Map(jobs.map((j) => [j.id, j.customerName]));
  if (payRunRecords.length === 0) {
    return <Card className="shadow-none border-border/60 bg-transparent p-8 text-center text-sm text-muted-foreground">No pay runs released yet.</Card>;
  }
  const handleExport = () => {
    exportCsv(
      stampFilename("payruns_paid"),
      ["Pay run ID", "Released at", "Job ID", "Party kind", "Party", "Released by", "Net released"],
      [...payRunRecords].reverse().map((r) => [
        r.id, r.releasedAt, r.jobId, r.partyKind, r.view.partyName ?? "", r.releasedBy, r.netReleased,
      ]),
    );
  };
  return (
    <Card className="shadow-none border-border/60 bg-transparent p-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <div className="text-[11px] text-muted-foreground">{payRunRecords.length} pay run{payRunRecords.length === 1 ? "" : "s"} released</div>
        <Button size="sm" variant="ghost" onClick={handleExport} title="Export rows as CSV">
          <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Released</TableHead>
            <TableHead>Job</TableHead>
            <TableHead>Party</TableHead>
            <TableHead>Released by</TableHead>
            <TableHead>Ledger lines</TableHead>
            <TableHead className="text-right">Net</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {[...payRunRecords].reverse().map((r) => (
            <TableRow
              key={r.id}
              className="cursor-pointer hover:bg-muted/40"
              onClick={() => onSelect(r, customerByJob.get(r.jobId) ?? r.jobId)}
              title="Open pay run detail"
            >
              <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">
                {format(new Date(r.releasedAt), "MMM d, h:mm a")}
              </TableCell>
              <TableCell className="font-mono text-[11px]">{r.jobId}</TableCell>
              <TableCell className="text-[11px]">{r.view.partyName ?? PARTY_LABEL[r.partyKind]}</TableCell>
              <TableCell className="text-[11px]">{r.releasedBy}</TableCell>
              <TableCell className="text-[11px]">
                <div className="flex flex-wrap gap-1">
                  {r.ledgerLines.map((l, i) => (
                    <Badge key={i} variant="outline" className="text-[10px]" title={l.memo}>
                      {l.category} · ${(l.amountCents / 100).toLocaleString()}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right font-mono text-[12px] font-semibold">
                ${r.netReleased.toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <tfoot className="border-t border-border/60 bg-muted/30">
          {(() => {
            const tNet = payRunRecords.reduce((s, r) => s + r.netReleased, 0);
            return (
              <tr className="text-[11px]">
                <td className="py-2.5 px-4 font-medium text-muted-foreground" colSpan={5}>
                  Released to date · {payRunRecords.length} pay run{payRunRecords.length === 1 ? "" : "s"}
                </td>
                <td className="py-2.5 px-4 text-right font-mono tabular-nums font-semibold text-foreground">
                  ${tNet.toLocaleString()}
                </td>
              </tr>
            );
          })()}
        </tfoot>
      </Table>
    </Card>
  );
}


function ApprovalsTable({
  pending, jobs, canApprove, onDecide,
}: {
  pending: FieldAddSubmission[];
  jobs: { id: string; customerName: string }[];
  canApprove: boolean;
  onDecide: (sub: FieldAddSubmission, decision: "approved" | "rejected") => void;
}) {
  const { payProfiles } = useAppData();
  if (pending.length === 0) {
    return <Card className="shadow-none border-border/60 bg-transparent p-8 text-center text-sm text-muted-foreground">No field-adds awaiting approval.</Card>;
  }
  const jobById = new Map(jobs.map((j) => [j.id, j.customerName]));
  // Sort: above-cap first (the risk the CFO actually needs to triage),
  // then by amount desc. Submission order is a tiebreaker only.
  const capFor = (s: FieldAddSubmission) =>
    s.partyId ? getPayProfile(payProfiles, s.partyId)?.fieldAddCapAmount ?? 0 : 0;
  const sorted = [...pending].sort((a, b) => {
    const aOver = capFor(a) > 0 && a.amount > capFor(a) ? 1 : 0;
    const bOver = capFor(b) > 0 && b.amount > capFor(b) ? 1 : 0;
    if (aOver !== bOver) return bOver - aOver;
    return b.amount - a.amount;
  });
  const handleExport = () => {
    exportCsv(
      stampFilename("payruns_approvals"),
      ["Submission ID", "Submitted at", "Job ID", "Customer", "Party ID", "Reason", "Amount", "Cap", "Above cap", "Submitted by"],
      pending.map((s) => {
        const cap = s.partyId ? getPayProfile(payProfiles, s.partyId)?.fieldAddCapAmount ?? 0 : 0;
        return [
          s.id, s.createdAt, s.jobId, jobById.get(s.jobId) ?? "", s.partyId ?? "",
          s.reason, s.amount, cap, cap > 0 && s.amount > cap ? "yes" : "no", s.createdBy,
        ];
      }),
    );
  };
  return (
    <Card className="shadow-none border-border/60 bg-transparent p-0 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <div className="text-[11px] text-muted-foreground">{pending.length} field-add{pending.length === 1 ? "" : "s"} pending</div>
        <Button size="sm" variant="ghost" onClick={handleExport} title="Export rows as CSV">
          <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Submitted</TableHead>
            <TableHead>Job</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead className="text-right">Amount</TableHead>
            <TableHead>Tier</TableHead>
            <TableHead>Submitted by</TableHead>
            <TableHead className="text-right"> </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((s) => {
            const cap = s.partyId ? getPayProfile(payProfiles, s.partyId)?.fieldAddCapAmount ?? 0 : 0;
            const overCap = cap > 0 && s.amount > cap;
            return (
              <TableRow key={s.id}>
                <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {format(new Date(s.createdAt), "MMM d, h:mm a")}
                </TableCell>
                <TableCell>
                  <div className="font-medium text-[12px]">{jobById.get(s.jobId) ?? s.jobId}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">{s.jobId}</div>
                </TableCell>
                <TableCell className="text-[11px] max-w-xs truncate" title={s.reason}>{s.reason}</TableCell>
                <TableCell className="text-right font-mono text-[12px] font-semibold">
                  ${s.amount.toLocaleString()}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={`text-[10px] ${overCap ? "border-warning text-warning" : "border-muted text-muted-foreground"}`}>
                    {overCap ? "Above cap" : "Within cap"}
                  </Badge>
                </TableCell>
                <TableCell className="text-[11px]">{s.createdBy}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm" variant="outline" disabled={!canApprove}
                      onClick={() => onDecide(s, "rejected")}
                      title={canApprove ? "Reject field-add" : "Insufficient role"}
                    >
                      <ThumbsDown className="h-3.5 w-3.5 mr-1" /> Reject
                    </Button>
                    <Button
                      size="sm" disabled={!canApprove}
                      onClick={() => onDecide(s, "approved")}
                      title={canApprove ? "Approve field-add" : "Insufficient role"}
                      data-testid={`fieldadd-approve-${s.id}`}
                    >
                      <ThumbsUp className="h-3.5 w-3.5 mr-1" /> Approve
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <tfoot className="border-t border-border/60 bg-muted/30">
          {(() => {
            const tAmt = pending.reduce((s, p) => s + p.amount, 0);
            const overCapN = pending.filter((p) => {
              const cap = p.partyId ? getPayProfile(payProfiles, p.partyId)?.fieldAddCapAmount ?? 0 : 0;
              return cap > 0 && p.amount > cap;
            }).length;
            return (
              <tr className="text-[11px]">
                <td className="py-2.5 px-4 font-medium text-muted-foreground" colSpan={3}>
                  Pending · {pending.length} field-add{pending.length === 1 ? "" : "s"}
                  {overCapN > 0 ? ` · ${overCapN} above cap` : ""}
                </td>
                <td className="py-2.5 px-4 text-right font-mono tabular-nums font-semibold text-foreground">
                  ${tAmt.toLocaleString()}
                </td>
                <td colSpan={3} />
              </tr>
            );
          })()}
        </tfoot>
      </Table>
    </Card>
  );
}


// ── PR8 · Variance + Reconciliation subview ─────────────────────────────

const SEVERITY_CLASS: Record<VarianceSeverity, string> = {
  none: "border-muted text-muted-foreground",
  info: "border-muted text-muted-foreground",
  warn: "border-warning text-warning",
  critical: "border-destructive text-destructive",
};

function fmtMoney(n: number | null): string {
  if (n == null) return "—";
  const sign = n < 0 ? "−" : "";
  return `${sign}$${Math.abs(n).toLocaleString()}`;
}

function fmtPct(n: number | null): string {
  if (n == null) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${Math.round(n * 100)}%`;
}

function VariancePanel({
  rollup,
  laborSignals,
  reconciliations,
  payRunRecords,
  jobs,
}: {
  rollup: VarianceRollup;
  laborSignals: Array<{ row: Row; signal: LaborVarianceSignal }>;
  reconciliations: PayRunReconciliation[];
  payRunRecords: PayRunRecord[];
  jobs: Job[];
}) {
  const recordById = new Map(payRunRecords.map((r) => [r.id, r]));
  const customerByJob = new Map(jobs.map((j) => [j.id, j.customerName]));
  const rowByJobId = new Map(
    laborSignals.map(({ row }) => [row.job.id, row] as const),
  );
  const [detail, setDetail] = useState<VarianceDetailContext | null>(null);
  const openLabor = (row: Row, signal: LaborVarianceSignal) =>
    setDetail({ kind: "labor", row, signal });
  const openRecon = (reconciliation: PayRunReconciliation) =>
    setDetail({
      kind: "reconciliation",
      reconciliation,
      record: recordById.get(reconciliation.payRunId),
      row: rowByJobId.get(reconciliation.jobId),
      customerName: customerByJob.get(reconciliation.jobId) ?? "—",
    });
  const nothingToShow =
    laborSignals.length === 0 && reconciliations.length === 0;


  if (nothingToShow) {
    return (
      <Card className="shadow-none border-border/60 bg-transparent p-8 text-center text-sm text-muted-foreground">
        No variance or reconciliation drift detected. Estimated vs actual labor
        and released pay runs are in agreement.
      </Card>
    );
  }

  // Sort: critical → warn → info, then by absolute variance.
  const rankSev: Record<VarianceSeverity, number> = { critical: 3, warn: 2, info: 1, none: 0 };
  const sortedLabor = [...laborSignals].sort((a, b) => {
    const sd = rankSev[b.signal.severity] - rankSev[a.signal.severity];
    if (sd !== 0) return sd;
    return Math.abs(b.signal.variance ?? 0) - Math.abs(a.signal.variance ?? 0);
  });
  const sortedRecon = [...reconciliations].sort(
    (a, b) => rankSev[b.severity] - rankSev[a.severity],
  );

  return (
    <div className="space-y-4">
      {/* Rollup */}
      <Card className="shadow-none border-border/60 bg-transparent p-4 grid grid-cols-2 md:grid-cols-5 gap-4">
        <Metric label="Labor compared" value={rollup.jobsWithLaborComparison} />
        <Metric label="Labor critical" value={rollup.laborCritical} tone={rollup.laborCritical > 0 ? "critical" : "none"} />
        <Metric label="Labor warn" value={rollup.laborWarn} tone={rollup.laborWarn > 0 ? "warn" : "none"} />
        <Metric label="Released drift" value={rollup.reconciliationDrift} tone={rollup.reconciliationDrift > 0 ? "warn" : "none"} />
        <Metric label="Net delta" value={fmtMoney(rollup.totalNetDelta)} tone={Math.abs(rollup.totalNetDelta) > 0 ? "warn" : "none"} />
      </Card>

      {/* Labor variance table */}
      {sortedLabor.length > 0 && (
        <Card className="shadow-none border-border/60 bg-transparent p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="text-xs font-semibold text-muted-foreground">Labor variance · estimated vs actual</div>
            <Button
              size="sm" variant="ghost"
              title="Export rows as CSV"
              onClick={() => exportCsv(
                stampFilename("payruns_variance_labor"),
                ["Job ID", "Customer", "Party kind", "Estimated", "Actual", "Delta $", "Delta %", "Severity", "Direction"],
                sortedLabor.map(({ row, signal }) => [
                  row.job.id, row.job.customerName, row.view.partyKind,
                  signal.estimated ?? "", signal.actual ?? "", signal.variance ?? "",
                  signal.variancePct ?? "", signal.severity, signal.direction ?? "",
                ]),
              )}
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job</TableHead>
                <TableHead>Party</TableHead>
                <TableHead className="text-right">Estimated</TableHead>
                <TableHead className="text-right">Actual</TableHead>
                <TableHead className="text-right">Δ $</TableHead>
                <TableHead className="text-right">Δ %</TableHead>
                <TableHead>Severity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedLabor.map(({ row, signal }) => (
                <TableRow
                  key={`var-${row.job.id}`}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => openLabor(row, signal)}
                  title="Open variance detail"
                >
                  <TableCell>
                    <div className="font-medium text-[12px] text-foreground hover:underline">
                      {row.job.customerName}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">{row.job.id}</div>
                  </TableCell>
                  <TableCell className="text-[11px]">{PARTY_LABEL[row.view.partyKind]}</TableCell>
                  <TableCell className="text-right font-mono text-[11px]">{fmtMoney(signal.estimated)}</TableCell>
                  <TableCell className="text-right font-mono text-[11px]">{fmtMoney(signal.actual)}</TableCell>
                  <TableCell className="text-right font-mono text-[11px]">{fmtMoney(signal.variance)}</TableCell>
                  <TableCell className="text-right font-mono text-[11px]">{fmtPct(signal.variancePct)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${SEVERITY_CLASS[signal.severity]}`}>
                      {signal.severity}{signal.direction ? ` · ${signal.direction}` : ""}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <tfoot className="border-t border-border/60 bg-muted/30">
              {(() => {
                const sumDelta = sortedLabor.reduce((s, x) => s + (x.signal.variance ?? 0), 0);
                const critN = sortedLabor.filter((x) => x.signal.severity === "critical").length;
                const warnN = sortedLabor.filter((x) => x.signal.severity === "warn").length;
                const deltaTone =
                  critN > 0 ? "text-destructive font-semibold"
                  : warnN > 0 ? "text-warning"
                  : "text-foreground";
                return (
                  <tr className="text-[11px]">
                    <td className="py-2.5 px-4 font-medium text-muted-foreground" colSpan={2}>
                      Total · {sortedLabor.length} job{sortedLabor.length === 1 ? "" : "s"}
                      {critN > 0 ? ` · ${critN} critical` : ""}
                      {warnN > 0 ? ` · ${warnN} warn` : ""}
                    </td>
                    <td />
                    <td />
                    <td className={`py-2.5 px-4 text-right font-mono tabular-nums ${deltaTone}`}>
                      {fmtMoney(sumDelta)}
                    </td>
                    <td />
                    <td />
                  </tr>
                );
              })()}
            </tfoot>
          </Table>
        </Card>
      )}


      {/* Reconciliation drift table */}
      {sortedRecon.length > 0 && (
        <Card className="shadow-none border-border/60 bg-transparent p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="text-xs font-semibold text-muted-foreground">Reconciliation drift · released pay runs that no longer match</div>
            <Button
              size="sm" variant="ghost"
              title="Export rows as CSV"
              onClick={() => exportCsv(
                stampFilename("payruns_variance_reconciliation"),
                ["Pay run ID", "Job ID", "Customer", "Released net", "Current net", "Delta", "Severity", "Findings"],
                sortedRecon.map((r) => [
                  r.payRunId, r.jobId, customerByJob.get(r.jobId) ?? "",
                  r.releasedNet ?? "", r.currentNet ?? "", r.netDelta ?? "", r.severity,
                  r.findings.map((f) => DRIFT_KIND_LABEL[f.kind]).join(" · "),
                ]),
              )}
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Job · Customer</TableHead>
                <TableHead>Pay run</TableHead>
                <TableHead>Released</TableHead>
                <TableHead>Findings</TableHead>
                <TableHead className="text-right">Released net</TableHead>
                <TableHead className="text-right">Current net</TableHead>
                <TableHead className="text-right">Δ</TableHead>
                <TableHead>Severity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRecon.map((r) => {
                const record = recordById.get(r.payRunId);
                const customer = customerByJob.get(r.jobId) ?? "—";
                return (
                <TableRow
                  key={r.payRunId}
                  className="cursor-pointer hover:bg-muted/40"
                  onClick={() => openRecon(r)}
                  title="Open reconciliation detail"
                >
                  <TableCell>
                    <div className="font-medium text-[12px] text-foreground hover:underline">
                      {customer}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">{r.jobId}</div>
                  </TableCell>
                  <TableCell className="font-mono text-[10px] text-muted-foreground">{r.payRunId}</TableCell>
                  <TableCell className="text-[11px]">
                    {record ? (
                      <>
                        <div className="text-foreground">{format(new Date(record.releasedAt), "MMM d, yyyy")}</div>
                        <div className="text-[10px] text-muted-foreground">by {record.releasedBy}</div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {r.findings.map((f, i) => (
                        <Badge
                          key={i}
                          variant="outline"
                          className={`text-[10px] ${SEVERITY_CLASS[f.severity]}`}
                          title={f.deltaAmount != null ? `Δ ${fmtMoney(f.deltaAmount)}` : undefined}
                        >
                          {DRIFT_KIND_LABEL[f.kind]}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-[11px]">{fmtMoney(r.releasedNet)}</TableCell>
                  <TableCell className="text-right font-mono text-[11px]">{fmtMoney(r.currentNet)}</TableCell>
                  <TableCell className="text-right font-mono text-[11px]">{fmtMoney(r.netDelta)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] ${SEVERITY_CLASS[r.severity]}`}>
                      {r.severity}
                    </Badge>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
            <tfoot className="border-t border-border/60 bg-muted/30">
              {(() => {
                const sumReleased = sortedRecon.reduce((s, r) => s + (r.releasedNet ?? 0), 0);
                const sumCurrent = sortedRecon.reduce((s, r) => s + (r.currentNet ?? 0), 0);
                const sumDelta = sortedRecon.reduce((s, r) => s + (r.netDelta ?? 0), 0);
                const critN = sortedRecon.filter((r) => r.severity === "critical").length;
                const warnN = sortedRecon.filter((r) => r.severity === "warn").length;
                const deltaTone =
                  critN > 0 ? "text-destructive font-semibold"
                  : warnN > 0 ? "text-warning"
                  : "text-foreground";
                return (
                  <tr className="text-[11px]">
                    <td className="py-2.5 px-4 font-medium text-muted-foreground" colSpan={4}>
                      Total · {sortedRecon.length} pay run{sortedRecon.length === 1 ? "" : "s"}
                      {critN > 0 ? ` · ${critN} critical` : ""}
                      {warnN > 0 ? ` · ${warnN} warn` : ""}
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono tabular-nums text-muted-foreground">
                      {fmtMoney(sumReleased)}
                    </td>
                    <td className="py-2.5 px-4 text-right font-mono tabular-nums text-muted-foreground">
                      {fmtMoney(sumCurrent)}
                    </td>
                    <td className={`py-2.5 px-4 text-right font-mono tabular-nums ${deltaTone}`}>
                      {fmtMoney(sumDelta)}
                    </td>
                    <td />
                  </tr>
                );
              })()}
            </tfoot>
          </Table>
        </Card>
      )}

      <VarianceDetailDrawer
        open={detail != null}
        onOpenChange={(o) => { if (!o) setDetail(null); }}
        context={detail}
      />
    </div>
  );
}

/**
 * RollupCell — clickable dollar tile in the page header strip.
 * Pure presentation; the click delegates to setPivot so a CFO can
 * triage between pivots without leaving the page header.
 */
function RollupCell({
  label, value, sub, tone, onClick,
}: {
  label: string;
  value: string;
  sub: string;
  tone: "foreground" | "muted" | "warn" | "critical";
  onClick: () => void;
}) {
  const toneClass =
    tone === "critical" ? "text-destructive"
    : tone === "warn" ? "text-warning"
    : tone === "muted" ? "text-muted-foreground"
    : "text-foreground";
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-md px-2 py-1 -mx-2 -my-1 hover:bg-muted/40 transition-colors"
      title={`Jump to ${label}`}
    >
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${toneClass}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{sub}</div>
    </button>
  );
}


function Metric({
  label, value, tone = "none",
}: { label: string; value: number | string; tone?: VarianceSeverity }) {
  const toneClass =
    tone === "critical" ? "text-destructive"
    : tone === "warn" ? "text-warning"
    : "text-foreground";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}

